import React, {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Stack, Typography} from '@mui/material';
import {Sx} from 'app/types/common';
import {DeviceApiService} from 'app/services/api/device/DeviceApiService';
import {createDeviceModel} from 'app/components/DeviceDetails/Models/Fabric';
import {Files} from 'app/components/entities/files';
import {useTeamFiles} from 'app/components/features/files';
import {FilesLibrary} from 'app/components/widgets/files';

const emptyList: Files.Recording[] = [];

type Props = Sx & {teamId: string};

export function TeamFiles({sx, teamId}: Props) {
  const {data: recordings = emptyList, isSuccess} = useTeamFiles({teamId, enabled: true});

  const deviceIds = useMemo(() => recordings?.map((r) => r.deviceId) ?? [], [recordings]);

  const {data: devices} = useQuery({
    queryKey: ['files', 'devices', teamId, deviceIds],
    queryFn: async () => {
      const contract = await DeviceApiService.getUnits(deviceIds);
      const mapped = contract.map((d) => createDeviceModel(d));
      return new Map(mapped.map((m) => [m.getId(), m]));
    },
    enabled: deviceIds.length > 0,
  });

  return (
    <Stack sx={sx}>
      <Typography fontWeight={600} variant="h5" mb={0.5}>
        Recordings
      </Typography>

      <Typography mb={1} color="text.secondary">
        All recordings that have been uploaded to your team appear here
      </Typography>

      {isSuccess ? (
        <FilesLibrary
          teamId={teamId}
          recordings={recordings}
          devices={devices}
          displayDevice={true}
        />
      ) : (
        <Typography mt={5} textAlign="center" variant="h6" color="text.secondary">
          Loading team recordings
        </Typography>
      )}
    </Stack>
  );
}
